import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, Clock, AlertTriangle, CheckCircle2, Loader2, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Analytics = ({ project }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allTasks, setAllTasks] = useState([]);
  const [codeObjects, setCodeObjects] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [project.id]);

  const fetchAnalytics = async () => {
    try {
      const [statsRes, tasksRes, codeRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/stats`),
        axios.get(`${API}/projects/${project.id}/workplan-tasks`),
        axios.get(`${API}/projects/${project.id}/custom-code`)
      ]);
      setStats(statsRes.data);
      setAllTasks(tasksRes.data);
      setCodeObjects(codeRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const completionRate = stats.total_tasks > 0 ? Math.round((stats.tasks_completed / stats.total_tasks) * 100) : 0;
  
  // Priority breakdown
  const priorityBreakdown = codeObjects.reduce((acc, obj) => {
    acc[obj.priority] = (acc[obj.priority] || 0) + 1;
    return acc;
  }, {});

  // Task type breakdown
  const taskTypeBreakdown = allTasks.reduce((acc, task) => {
    acc[task.task_type] = (acc[task.task_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center space-x-3 mb-4">
          <BarChart3 className="w-10 h-10" />
          <div>
            <h2 className="text-3xl font-bold">Analytics Dashboard</h2>
            <p className="text-blue-100 mt-1">Comprehensive project insights and metrics</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-6">
        <MetricCard
          title="Completion Rate"
          value={`${completionRate}%`}
          subtitle={`${stats.tasks_completed}/${stats.total_tasks} tasks`}
          icon={CheckCircle2}
          color="green"
          trend="+5%"
        />
        <MetricCard
          title="Total Effort"
          value={`${stats.total_effort_days}d`}
          subtitle={`${stats.total_effort_hours} hours`}
          icon={Clock}
          color="blue"
        />
        <MetricCard
          title="High Priority Items"
          value={stats.high_priority_objects}
          subtitle="Requires attention"
          icon={AlertTriangle}
          color="orange"
        />
        <MetricCard
          title="Test Coverage"
          value={stats.test_cases}
          subtitle="Test cases"
          icon={Target}
          color="purple"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Priority Distribution */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-6 h-6 mr-3 text-blue-600" />
            Priority Distribution
          </h3>
          <div className="space-y-4">
            {Object.entries(priorityBreakdown).map(([priority, count]) => {
              const total = Object.values(priorityBreakdown).reduce((a, b) => a + b, 0);
              const percentage = Math.round((count / total) * 100);
              const colors = {
                critical: 'bg-red-500',
                high: 'bg-orange-500',
                medium: 'bg-yellow-500',
                low: 'bg-green-500'
              };
              return (
                <div key={priority}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700 capitalize">{priority}</span>
                    <span className="text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`${colors[priority]} h-3 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Task Type Distribution */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <BarChart3 className="w-6 h-6 mr-3 text-purple-600" />
            Task Types
          </h3>
          <div className="space-y-4">
            {Object.entries(taskTypeBreakdown).map(([type, count]) => {
              const total = Object.values(taskTypeBreakdown).reduce((a, b) => a + b, 0);
              const percentage = Math.round((count / total) * 100);
              const colors = {
                remediation: 'bg-blue-500',
                testing: 'bg-purple-500',
                configuration: 'bg-green-500',
                deployment: 'bg-orange-500'
              };
              return (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700 capitalize">{type}</span>
                    <span className="text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`${colors[type]} h-3 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Project Breakdown</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Custom Code Objects</p>
            <p className="text-3xl font-bold text-gray-900">{stats.custom_code_objects}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Simplification Items</p>
            <p className="text-3xl font-bold text-gray-900">{stats.simplification_items}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Impact Analyses</p>
            <p className="text-3xl font-bold text-gray-900">{stats.impact_analyses}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
  const colorClasses = {
    green: 'from-green-500 to-emerald-600',
    blue: 'from-blue-500 to-cyan-600',
    orange: 'from-orange-500 to-red-600',
    purple: 'from-purple-500 to-pink-600'
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${colorClasses[color]} rounded-xl flex items-center justify-center shadow-md`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
            {trend}
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );
};
